/**
 * Voice Speaker Button Component
 * Professional speaker button for AI message bubbles with TTS functionality
 * 
 * @author Software Engineering Team
 * @version 1.0.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Box
} from '@mui/material';
import {
  VolumeUp as SpeakerIcon,
  VolumeOff as SpeakerOffIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';

import { getTTSManager } from '../services/VoiceTTSManager';
import './VoiceSpeakerButton.css';

const VoiceSpeakerButton = ({ 
  messageId,
  text, 
  onError,
  disabled = false,
  size = 'small',
  className = ''
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [ttsConfig, setTtsConfig] = useState({
    rate: 1.0,
    pitch: 1.0,
    volume: 1.0,
    voiceURI: null
  });
  const [availableVoices, setAvailableVoices] = useState([]);
  
  const ttsManager = getTTSManager();

  /**
   * Initialize component
   */
  useEffect(() => {
    initializeComponent();
    return () => {
      // Cleanup when component unmounts
      if (ttsManager) {
        ttsManager.unregisterMessage(messageId);
      }
    };
  }, [messageId, text]);

  /**
   * Initialize TTS for this message
   */
  const initializeComponent = useCallback(async () => {
    try {
      if (!ttsManager) {
        throw new Error('TTS Manager not available');
      }

      // Register this message with TTS manager
      ttsManager.registerMessage(messageId, text, {
        onStart: handleSpeechStart,
        onEnd: handleSpeechEnd,
        onError: handleSpeechError,
        onPause: handleSpeechPause,
        onResume: handleSpeechResume
      });

      // Load available voices
      const voices = ttsManager.getVoices();
      setAvailableVoices(voices);

      // Update TTS config
      ttsManager.updateConfig(ttsConfig);

    } catch (error) {
      console.error('Failed to initialize TTS for message:', messageId, error);
      setError(error);
    }
  }, [messageId, text, ttsConfig]);

  /**
   * Handle speech start
   */
  const handleSpeechStart = useCallback(() => {
    setIsPlaying(true);
    setIsPaused(false);
    setIsLoading(false);
    console.info(`Speech started for message ${messageId}`);
  }, [messageId]);

  /**
   * Handle speech end
   */
  const handleSpeechEnd = useCallback(() => {
    setIsPlaying(false);
    setIsPaused(false);
    setIsLoading(false);
    console.info(`Speech ended for message ${messageId}`);
  }, [messageId]);

  /**
   * Handle speech error
   */
  const handleSpeechError = useCallback((error) => {
    setIsPlaying(false);
    setIsPaused(false);
    setIsLoading(false);
    setError(error);
    console.error(`Speech error for message ${messageId}:`, error);
    onError?.(error);
  }, [messageId, onError]);

  /**
   * Handle speech pause
   */
  const handleSpeechPause = useCallback(() => {
    setIsPaused(true);
    console.info(`Speech paused for message ${messageId}`);
  }, [messageId]);

  /**
   * Handle speech resume
   */
  const handleSpeechResume = useCallback(() => {
    setIsPaused(false);
    console.info(`Speech resumed for message ${messageId}`);
  }, [messageId]);

  /**
   * Toggle speech playback
   */
  const toggleSpeech = useCallback(async () => {
    try {
      if (disabled || !ttsManager) return;

      setIsLoading(true);
      setError(null);

      if (isPlaying) {
        if (isPaused) {
          // Resume
          ttsManager.resume();
        } else {
          // Pause
          ttsManager.pause();
        }
      } else {
        // Start speaking
        await ttsManager.speak(messageId);
      }

    } catch (error) {
      console.error('Failed to toggle speech:', error);
      handleSpeechError(error);
    }
  }, [disabled, ttsManager, messageId, isPlaying, isPaused]);

  /**
   * Stop speech
   */
  const stopSpeech = useCallback(() => {
    if (ttsManager && isPlaying) {
      ttsManager.stop();
    }
  }, [ttsManager, isPlaying]);

  /**
   * Handle settings dialog
   */
  const openSettings = useCallback((event) => {
    event.stopPropagation();
    setShowSettings(true);
  }, []);

  const closeSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  /**
   * Update TTS configuration
   */
  const updateTTSConfig = useCallback((newConfig) => {
    setTtsConfig(prev => ({ ...prev, ...newConfig }));
    if (ttsManager) {
      ttsManager.updateConfig({ ...ttsConfig, ...newConfig });
    }
  }, [ttsManager, ttsConfig]);

  /**
   * Get button icon based on state
   */
  const getButtonIcon = () => {
    if (isLoading) {
      return <CircularProgress size={16} color="inherit" />;
    }
    if (isPlaying && !isPaused) {
      return <PauseIcon />;
    }
    if (isPlaying && isPaused) {
      return <PlayIcon />;
    }
    if (error) {
      return <SpeakerOffIcon />;
    }
    return <SpeakerIcon />;
  };

  /**
   * Get button color based on state
   */
  const getButtonColor = () => {
    if (isPlaying) return 'primary';
    if (error) return 'error';
    return 'default';
  };

  /**
   * Get tooltip text
   */
  const getTooltipText = () => {
    if (disabled) return 'Voice output disabled';
    if (isLoading) return 'Loading...';
    if (error) return 'Voice unavailable - click for settings';
    if (isPlaying && isPaused) return 'Resume speaking';
    if (isPlaying) return 'Pause speaking';
    return 'Click to hear this message';
  };

  return (
    <>
      <div className={`voice-speaker-container ${className}`}>
        <Tooltip title={getTooltipText()} arrow>
          <span>
            <IconButton
              onClick={toggleSpeech}
              disabled={disabled || isLoading}
              color={getButtonColor()}
              size={size}
              className={`voice-speaker-button ${isPlaying ? 'speaking' : ''}`}
              aria-label={isPlaying ? 'Pause speech' : 'Start speech'}
            >
              {getButtonIcon()}
            </IconButton>
          </span>
        </Tooltip>

        {/* Settings button - only show when not playing */}
        {!isPlaying && availableVoices.length > 0 && (
          <Tooltip title="Voice settings" arrow>
            <IconButton
              onClick={openSettings}
              size="small"
              className="voice-settings-button"
              aria-label="Voice settings"
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* Stop button - only show when playing */}
        {isPlaying && (
          <Tooltip title="Stop speaking" arrow>
            <IconButton
              onClick={stopSpeech}
              size="small"
              color="secondary"
              className="voice-stop-button"
              aria-label="Stop speech"
            >
              <StopIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </div>

      {/* Voice Settings Dialog */}
      <Dialog
        open={showSettings}
        onClose={closeSettings}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Voice Settings
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error.message || 'Voice synthesis is not available in your browser.'}
            </Alert>
          )}

          {/* Voice Selection */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Voice</InputLabel>
            <Select
              value={ttsConfig.voiceURI || ''}
              onChange={(e) => updateTTSConfig({ voiceURI: e.target.value })}
              label="Voice"
            >
              <MenuItem value="">
                <em>Default Voice</em>
              </MenuItem>
              {availableVoices.map((voice) => (
                <MenuItem key={voice.voiceURI} value={voice.voiceURI}>
                  {voice.name} ({voice.lang})
                  {voice.localService && ' - Local'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Speech Rate */}
          <Box sx={{ mb: 3 }}>
            <Typography gutterBottom>
              Speech Rate: {ttsConfig.rate.toFixed(1)}x
            </Typography>
            <Slider
              value={ttsConfig.rate}
              onChange={(_, value) => updateTTSConfig({ rate: value })}
              min={0.5}
              max={2.0}
              step={0.1}
              marks={[
                { value: 0.5, label: '0.5x' },
                { value: 1.0, label: '1x' },
                { value: 2.0, label: '2x' }
              ]}
            />
          </Box>

          {/* Pitch */}
          <Box sx={{ mb: 3 }}>
            <Typography gutterBottom>
              Pitch: {ttsConfig.pitch.toFixed(1)}
            </Typography>
            <Slider
              value={ttsConfig.pitch}
              onChange={(_, value) => updateTTSConfig({ pitch: value })}
              min={0.5}
              max={2.0}
              step={0.1}
              marks={[
                { value: 0.5, label: 'Low' },
                { value: 1.0, label: 'Normal' },
                { value: 2.0, label: 'High' }
              ]}
            />
          </Box>

          {/* Volume */}
          <Box sx={{ mb: 2 }}>
            <Typography gutterBottom>
              Volume: {Math.round(ttsConfig.volume * 100)}%
            </Typography>
            <Slider
              value={ttsConfig.volume}
              onChange={(_, value) => updateTTSConfig({ volume: value })}
              min={0.1}
              max={1.0}
              step={0.1}
              marks={[
                { value: 0.1, label: '10%' },
                { value: 0.5, label: '50%' },
                { value: 1.0, label: '100%' }
              ]}
            />
          </Box>

        </DialogContent>
        <DialogActions>
          <Button onClick={closeSettings}>
            Close
          </Button>
          <Button 
            onClick={() => {
              closeSettings();
              toggleSpeech();
            }} 
            variant="contained"
            disabled={!!error}
          >
            Test Voice
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VoiceSpeakerButton;
